/**
 * Configuration validation.
 *
 * Configuration is edited by hand, so a single typo must not hide every other mistake.
 * Each collection is parsed independently and every problem is reported at once.
 *
 * A bad entry is dropped and reported rather than failing the whole configuration, so
 * the rest of the site still loads. Only a missing site block, UI block or usable
 * BlueMap server makes the app unusable, and that is signalled through `fatal`.
 */
import {
	bluemapServerSchema,
	isValidSlug,
	mapCategorySchema,
	mapSchema,
	markerCategorySchema,
	playerOverlaySchema,
	siteSchema,
	uiConfigSchema
} from './schema';
import type { BlueMapServer, MapCategory, MapConfig, RawConfig, ResolvedConfig } from './schema';

export interface ConfigIssue {
	/** Dotted path to the offending field, e.g. `maps.3.bluemap.mapId`. */
	path: string;
	message: string;
	/** `error` blocks the entity; `warning` reports something suspicious but usable. */
	severity: 'error' | 'warning';
}

export interface ConfigResult {
	/** Always usable, invalid entries have been dropped and reported. */
	config: ResolvedConfig;
	issues: ConfigIssue[];
	/** True when the essentials are missing and the app cannot render a map at all. */
	fatal: boolean;
}

/* -------------------------------------------------------------------------- */
/* Fallbacks                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Minimum viable blocks, built by parsing empty input so the schema's own defaults
 * are the single source of those values. Nothing here duplicates a default.
 */
const FALLBACK_SITE = siteSchema.parse({ name: 'Minecraft Map', theme: {} });
const FALLBACK_UI = uiConfigSchema.parse({ features: {} });
const FALLBACK_PLAYERS = playerOverlaySchema.parse({});

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The only thing this module needs from a Zod schema.
 *
 * Typed structurally rather than against Zod's own generics, which differ between
 * major versions and carry the input type along with the output.
 */
interface Parseable<TOutput> {
	safeParse(value: unknown):
		| { success: true; data: TOutput }
		| { success: false; error: { issues: readonly { path: readonly PropertyKey[]; message: string }[] } };
}

/** Flattens a Zod error into reportable issues, prefixing each with its collection. */
function zodIssues(
	error: { issues: readonly { path: readonly PropertyKey[]; message: string }[] },
	prefix: string,
	severity: ConfigIssue['severity'] = 'error'
): ConfigIssue[] {
	return error.issues.map((issue) => {
		const path = [prefix, ...issue.path.map(String)].filter(Boolean).join('.');
		return { path, message: issue.message, severity };
	});
}

/**
 * Parses a list of entities one by one, keeping the valid ones.
 *
 * Used for maps, categories and marker categories: one malformed entry costs that
 * entry only.
 */
function parseEach<TOutput>(
	items: readonly unknown[],
	schema: Parseable<TOutput>,
	collection: string,
	issues: ConfigIssue[]
): TOutput[] {
	const parsed: TOutput[] = [];

	items.forEach((item, index) => {
		const result = schema.safeParse(item);
		if (result.success) {
			parsed.push(result.data);
			return;
		}

		// `id` is a best-effort label: the entry may be too broken to have one.
		const id = (item as { id?: unknown } | null)?.id;
		const label = typeof id === 'string' ? `${collection}.${id}` : `${collection}.${index}`;
		issues.push(...zodIssues(result.error, label));
	});

	return parsed;
}

/** Reports duplicate ids and drops all but the first occurrence. */
function dedupeById<T extends { id: string }>(
	items: T[],
	collection: string,
	issues: ConfigIssue[]
): T[] {
	const seen = new Set<string>();

	return items.filter((item) => {
		if (seen.has(item.id)) {
			issues.push({
				path: `${collection}.${item.id}`,
				message: `duplicate id "${item.id}", the later entry is ignored`,
				severity: 'error'
			});
			return false;
		}
		seen.add(item.id);
		return true;
	});
}

/* -------------------------------------------------------------------------- */
/* Slugs                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Slugs that collide with something else served at the site root.
 *
 * Only names a slug *could* take appear here. `_`-prefixed paths are absent by design:
 * `slugSchema` requires a leading alphanumeric, so `/_app` (SvelteKit's assets) and
 * `/_bluemap` (the BlueMap proxy) cannot be claimed at all, and neither can a dotted
 * name such as `favicon.svg`.
 */
const RESERVED_SLUGS = new Set(['map']);

/**
 * Applies the URL-level rules to every parsed map, authored slug or derived.
 *
 * Runs over disabled maps too. They have no URL of their own, but a slug they hold must
 * stay unclaimed: otherwise flipping `enabled` to true later would collide with a map
 * that is already live, and the collision would surface as a surprise at that moment.
 *
 * Three failure modes, each reported rather than guessed at:
 * - a slug that is not a valid URL segment, which a *derived* slug can be (an authored
 *   one is checked by `slugSchema`), because it came from an id containing "." or "_";
 * - the reserved name `map`, where `/map/<id>` is the legacy redirect route;
 * - a slug another map already holds, where the later entry loses, as with duplicate ids.
 */
function filterBySlug(maps: MapConfig[], issues: ConfigIssue[]): MapConfig[] {
	const seen = new Set<string>();

	return maps.filter((map) => {
		if (!isValidSlug(map.slug)) {
			issues.push({
				path: `maps.${map.id}.slug`,
				message: `slug "${map.slug}" is not a usable URL segment, which it inherited from the id: give this map an explicit slug or rename its id`,
				severity: 'error'
			});
			return false;
		}

		if (RESERVED_SLUGS.has(map.slug)) {
			issues.push({
				path: `maps.${map.id}.slug`,
				message: `slug "${map.slug}" is reserved for the legacy redirect route, so this map would only open at "/${map.slug}/" while every path below it redirects`,
				severity: 'error'
			});
			return false;
		}

		if (seen.has(map.slug)) {
			issues.push({
				path: `maps.${map.id}.slug`,
				message: `duplicate slug "${map.slug}", the later entry is ignored`,
				severity: 'error'
			});
			return false;
		}

		seen.add(map.slug);
		return true;
	});
}

/* -------------------------------------------------------------------------- */
/* Main entry point                                                           */
/* -------------------------------------------------------------------------- */

export function parseConfig(raw: RawConfig): ConfigResult {
	const issues: ConfigIssue[] = [];

	/* --- Essentials: site, UI, players, servers ---------------------------- */

	const site = siteSchema.safeParse(raw.site);
	if (!site.success) issues.push(...zodIssues(site.error, 'site'));

	const ui = uiConfigSchema.safeParse(raw.ui);
	if (!ui.success) issues.push(...zodIssues(ui.error, 'ui'));

	const players = playerOverlaySchema.safeParse(raw.players);
	if (!players.success) issues.push(...zodIssues(players.error, 'players'));

	const servers = dedupeById(
		parseEach(raw.servers ?? [], bluemapServerSchema, 'servers', issues),
		'servers',
		issues
	);

	/* --- Collections: categories and maps --------------------------------- */

	const mapCategories = dedupeById(
		parseEach(raw.mapCategories ?? [], mapCategorySchema, 'mapCategories', issues),
		'mapCategories',
		issues
	);

	// Slugs are settled before the semantic passes that follow, so a map dropped for a
	// bad slug never reaches the default-map rules and cannot become the fallback.
	const maps = filterBySlug(
		dedupeById(parseEach(raw.maps ?? [], mapSchema, 'maps', issues), 'maps', issues),
		issues
	);

	const markerCategories = dedupeById(
		parseEach(raw.markerCategories ?? [], markerCategorySchema, 'markerCategories', issues),
		'markerCategories',
		issues
	);

	/* --- Semantic checks -------------------------------------------------- */

	const serverIds = new Set(servers.map((server: BlueMapServer) => server.id));
	const categoryIds = new Set(mapCategories.map((category: MapCategory) => category.id));

	const usableMaps = maps.filter((map: MapConfig) => {
		// A map may name a server, or carry its own baseUrl. Naming a server that does
		// not exist is almost always a typo, so the map is dropped rather than guessed at.
		const { server, baseUrl } = map.bluemap;

		if (server && !serverIds.has(server)) {
			issues.push({
				path: `maps.${map.id}.bluemap.server`,
				message: `unknown server "${server}", known servers: ${[...serverIds].join(', ') || 'none'}`,
				severity: 'error'
			});
			return false;
		}

		if (!server && !baseUrl) {
			issues.push({
				path: `maps.${map.id}.bluemap`,
				message: 'map has neither a server reference nor a baseUrl',
				severity: 'error'
			});
			return false;
		}

		if (map.category && !categoryIds.has(map.category)) {
			// Cosmetic rather than structural: the map still renders, just ungrouped.
			issues.push({
				path: `maps.${map.id}.category`,
				message: `unknown category "${map.category}", the map is shown without a group`,
				severity: 'warning'
			});
		}

		return true;
	});

	/* --- Default server --------------------------------------------------- */

	const defaultServers = servers.filter((server: BlueMapServer) => server.default);
	if (defaultServers.length > 1) {
		issues.push({
			path: 'servers',
			message: `${defaultServers.length} servers are marked default; the first one wins`,
			severity: 'warning'
		});
	}

	/* --- Default map ------------------------------------------------------ */

	const routable = usableMaps.filter((map) => map.enabled && map.selectable);
	const declaredDefaults = routable.filter((map) => map.default);

	if (declaredDefaults.length > 1) {
		issues.push({
			path: 'maps',
			message: `${declaredDefaults.length} maps are marked default (${declaredDefaults
				.map((map) => map.id)
				.join(', ')}); the first by order wins`,
			severity: 'warning'
		});
	}

	// A default that cannot be routed to would produce a broken landing page.
	for (const map of usableMaps) {
		if (map.default && !routable.includes(map)) {
			issues.push({
				path: `maps.${map.id}.default`,
				message: 'map is marked default but is not enabled and selectable; ignoring',
				severity: 'error'
			});
		}
	}

	if (routable.length > 0 && declaredDefaults.length === 0) {
		issues.push({
			path: 'maps',
			message: 'no enabled map is marked default; the first by order is used',
			severity: 'warning'
		});
	}

	if (usableMaps.length > 0 && routable.length === 0) {
		issues.push({
			path: 'maps',
			message: 'no enabled, selectable map is available, the map view has nothing to show',
			severity: 'error'
		});
	}

	/* --- Sibling dimensions ----------------------------------------------- */

	// Two maps claiming the same dimension within one group would make the dimension
	// switcher ambiguous, so it is worth flagging even though rendering still works.
	const groupDimensions = new Map<string, Set<string>>();
	for (const map of usableMaps) {
		if (!map.group || !map.dimension) continue;

		const seen = groupDimensions.get(map.group) ?? new Set<string>();
		if (seen.has(map.dimension)) {
			issues.push({
				path: `maps.${map.id}.dimension`,
				message: `group "${map.group}" already has a "${map.dimension}" map; the dimension switcher will only offer the first`,
				severity: 'warning'
			});
		}
		seen.add(map.dimension);
		groupDimensions.set(map.group, seen);
	}

	const fatal = servers.length === 0;

	return {
		config: {
			site: site.success ? site.data : FALLBACK_SITE,
			servers,
			mapCategories,
			maps: usableMaps,
			markerCategories,
			players: players.success ? players.data : FALLBACK_PLAYERS,
			ui: ui.success ? ui.data : FALLBACK_UI
		},
		issues,
		fatal
	};
}

/* -------------------------------------------------------------------------- */
/* Reporting                                                                  */
/* -------------------------------------------------------------------------- */

export function formatIssue(issue: ConfigIssue): string {
	const marker = issue.severity === 'error' ? '✖' : '⚠';
	return `${marker} ${issue.path}: ${issue.message}`;
}

/** Console diagnostics, printed once at startup in development. */
export function reportIssues(issues: ConfigIssue[]): void {
	if (issues.length === 0) return;

	const errors = issues.filter((issue) => issue.severity === 'error');
	const warnings = issues.filter((issue) => issue.severity === 'warning');

	console.groupCollapsed(`[config] ${errors.length} error(s), ${warnings.length} warning(s)`);
	for (const issue of issues) {
		console.warn(formatIssue(issue));
	}
	console.groupEnd();
}
